import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { isListingPath, saveListingUrl } from "@/lib/listing-history";
import LegacyRedirect, { LangSync } from "@/components/LegacyRedirects";
import { slugToType } from "@/lib/url-slugs";

// Scroll to top on route change — mobile only, smooth animation
function MobileScrollToTop() {
  const { pathname, search } = useLocation();

  // Remember last product-listing URL (with filters/sort/page) so the product
  // detail page can return the user to the exact context they came from.
  useEffect(() => {
    if (isListingPath(pathname)) {
      saveListingUrl(`${pathname}${search}`);
    }
  }, [pathname, search]);

  const scrollTopMobile = () => {
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };


  useEffect(() => {
    scrollTopMobile();
  }, [pathname, search]);

  // Also handle taps on links that point to the SAME route (no router navigation event)
  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!window.matchMedia("(max-width: 767px)").matches) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) {
        return;
      }

      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return;

      const currentRoute = `${window.location.pathname}${window.location.search}`;
      const targetRoute = `${url.pathname}${url.search}`;

      if (targetRoute === currentRoute) {
        scrollTopMobile();
      }
    };

    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [pathname, search]);

  return null;
}
import { CartProvider } from "@/context/CartContext";
import { SearchProvider } from "@/context/SearchContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { apiClient, unwrapApiResult, withAbortSignal } from "@/lib/api-client";
import MaintenancePage from "@/components/MaintenancePage";
import { shouldShowMaintenancePage } from "@/lib/environment";

import Header from "@/components/layout/Header";
import SaleBanner from "@/components/layout/SaleBanner";
import TopBar from "@/components/layout/TopBar";
import Footer from "@/components/layout/Footer";
import CartDrawer from "@/components/layout/CartDrawer";
import ScrollToTopButton from "@/components/layout/ScrollToTopButton";
import WhatsAppButton from "@/components/layout/WhatsAppButton";
import AiAssistant from "@/components/ai/AiAssistant";
import FontLoader from "@/components/FontLoader";
import { AssistantProvider } from "@/context/AssistantContext";

// Eagerly load the homepage for best LCP
import Index from "./pages/Index";

// Lazy-load all other routes to reduce initial JS bundle
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ShippingReturns = lazy(() => import("./pages/ShippingReturns"));
const Sale = lazy(() => import("./pages/Sale"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Pricing = lazy(() => import("./pages/Pricing"));
const GiftCard = lazy(() => import("./pages/GiftCard"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Courts = lazy(() => import("./pages/Courts"));
const StarviePlayer = lazy(() => import("./pages/StarviePlayer"));
const PlayerPage = lazy(() => import("./pages/PlayerPage"));

const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

// Prefetch core data on app mount so navigation is instant
function usePrefetch() {
  useEffect(() => {
    const prefetch = async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: ["site-settings"],
          queryFn: async ({ signal }) => {
            const result = await apiClient.query<any[]>({
              label: "prefetch-site-settings",
              routeTag: "App.usePrefetch",
              timeoutMs: 15_000,
              signal,
              run: (abortSignal) => withAbortSignal(
                supabase.from("site_settings" as any).select("key,value").order("key"),
                abortSignal
              ),
            });

            const rows = unwrapApiResult(result) ?? [];
            const map: Record<string, string> = {};
            for (const row of rows as any[]) {
              map[row.key] = row.value;
            }
            return map;
          },
          staleTime: 2 * 60 * 1000,
        });

        await queryClient.prefetchQuery({
          queryKey: ["categories", "all", "minimal"],
          queryFn: async ({ signal }) => {
            const result = await apiClient.query<any[]>({
              label: "prefetch-categories",
              routeTag: "App.usePrefetch",
              timeoutMs: 15_000,
              signal,
              run: (abortSignal) =>
                withAbortSignal(
                  supabase
                    .from("categories")
                    .select("id,name,name_en,name_es,slug,product_type,sort_order,is_active,created_at")
                    .eq("is_active", true)
                    .order("sort_order"),
                  abortSignal
                ),
            });
            return unwrapApiResult(result) ?? [];
          },
          staleTime: 2 * 60 * 1000,
        });

        await queryClient.prefetchQuery({
          queryKey: ["newest-products", 12],
          queryFn: async ({ signal }) => {
            const result = await apiClient.query<any[]>({
              label: "prefetch-newest-products",
              routeTag: "App.usePrefetch",
              timeoutMs: 15_000,
              signal,
              run: (abortSignal) =>
                withAbortSignal(
                  supabase
                    .from("products")
                    .select("id,name,slug,brand,price,compare_at_price,currency,stock,sku,status,category_id,description,specs,product_type")
                    .eq("status", "active")
                    .order("created_at", { ascending: false })
                    .limit(12),
                  abortSignal
                ),
            });
            return unwrapApiResult(result) ?? [];
          },
          staleTime: 2 * 60 * 1000,
        });
      } catch {
        // Prefetch failures are non-critical
      }
    };
    prefetch();

    // Recover from tab inactivity — refresh auth + selectively refetch stale queries.
    // IMPORTANT: never invalidate while the admin is typing in a form. Refetching
    // remounts data-driven editors and wipes unsaved input.
    let lastVisible = Date.now();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastVisible;
        supabase.auth.getSession().catch(() => {});
        const inAdmin = window.location.pathname.startsWith("/admin");
        const activeEl = document.activeElement as HTMLElement | null;
        const isTyping =
          !!activeEl &&
          (activeEl.tagName === "INPUT" ||
            activeEl.tagName === "TEXTAREA" ||
            activeEl.tagName === "SELECT" ||
            activeEl.isContentEditable);
        // Only invalidate if tab was hidden for more than 5 minutes,
        // and never while admin is editing a form.
        if (elapsed > 5 * 60 * 1000 && !inAdmin && !isTyping) {
          queryClient.invalidateQueries();
        }
        lastVisible = Date.now();
      } else {
        lastVisible = Date.now();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);
}

// Maintenance gate — wraps all routes
const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const showMaintenance = shouldShowMaintenancePage();

  // Block the live site immediately while auth initializes so public pages
  // never flash before the maintenance gate is applied.
  if (showMaintenance && (authLoading || !user)) {
    return <MaintenancePage />;
  }

  return <>{children}</>;
};

// Wrapper: a single dynamic path segment can be either a catalog type slug
// (e.g. "padel-raketes") or a product slug. Decide which page to render.
function CatalogOrProductRoute() {
  const { slug } = useParams<{ slug: string }>();
  return slug && slugToType(slug) ? <Products /> : <ProductDetail />;
}

// Legacy /p/:slug → /:lang/:slug (permanent client-side redirect)
function LegacyProductRedirect() {
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  return <Navigate to={`/${lang || "lv"}/${slug || ""}`} replace />;
}

// Two-stage routing: pathname-based switch ensures /admin/* is never outranked
// by /:lang/:typeSlug/:brandSlug (which would otherwise capture /admin/x/y
// with lang="admin" and cause a 404 on deep admin links).
function AppRoutes() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return (
      <Routes>
        <Route path="/admin/*" element={<Admin />} />
      </Routes>
    );
  }
  if (pathname === "/starvie-player" || pathname.startsWith("/starvie-player/")) {
    return (
      <Routes>
        <Route path="/starvie-player" element={<StarviePlayer />} />
      </Routes>
    );
  }
  // Stripe success URL is language-less (/order/success). Without this branch it
  // would be captured by /:lang/:slug and render "Produkts nav atrasts".
  if (pathname === "/order/success" || pathname === "/pasutijums/veiksmigs") {
    return (
      <Routes>
        <Route path="*" element={<OrderSuccess />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/:lang" element={<LangSync><Outlet /></LangSync>}>
        <Route index element={<Index />} />
        <Route path="p/:slug" element={<LegacyProductRedirect />} />

        {/* Static pages — LV slugs */}
        <Route path="grozs" element={<Cart />} />
        <Route path="ienakt" element={<Auth />} />
        <Route path="parole-atjaunot" element={<ResetPassword />} />
        <Route path="konts" element={<Account />} />
        <Route path="kontakti" element={<Contact />} />
        <Route path="biezi-uzdotie-jautajumi" element={<FAQ />} />
        <Route path="piegade-un-atgriesana" element={<ShippingReturns />} />
        <Route path="noteikumi" element={<Terms />} />
        <Route path="privatuma-politika" element={<Privacy />} />
        <Route path="izpardosana" element={<Sale />} />
        <Route path="davanu-karte" element={<GiftCard />} />
        <Route path="abonements" element={<Pricing />} />
        <Route path="jaunumi/:slug" element={<ArticleDetail />} />
        <Route path="pasutijums/veiksmigs" element={<OrderSuccess />} />
        <Route path="meklesana" element={<Products />} />
        <Route path="visi-produkti" element={<Products />} />
        <Route path="laukumi" element={<Courts />} />
        <Route path="players/:slug" element={<PlayerPage />} />

        {/* Static pages — EN slugs */}
        <Route path="cart" element={<Cart />} />
        <Route path="login" element={<Auth />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="account" element={<Account />} />
        <Route path="contact" element={<Contact />} />
        <Route path="faq" element={<FAQ />} />
        <Route path="shipping-returns" element={<ShippingReturns />} />
        <Route path="terms" element={<Terms />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="sale" element={<Sale />} />
        <Route path="gift-card" element={<GiftCard />} />
        <Route path="subscription" element={<Pricing />} />
        <Route path="news/:slug" element={<ArticleDetail />} />
        <Route path="order/success" element={<OrderSuccess />} />
        <Route path="search" element={<Products />} />
        <Route path="all-products" element={<Products />} />
        <Route path="courts" element={<Courts />} />
        <Route path="pro-players/:slug" element={<PlayerPage />} />

        <Route path=":typeSlug/:brandSlug" element={<Products />} />
        <Route path=":slug" element={<CatalogOrProductRoute />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
  );
}

function isCourtsPath(pathname: string) {
  return pathname.endsWith("/courts") || pathname.endsWith("/laukumi");
}

function SaleBannerGate() {
  const { pathname } = useLocation();
  if (isCourtsPath(pathname)) {
    return null;
  }
  return <SaleBanner />;
}

function HeaderGate() {
  const { pathname } = useLocation();
  if (isCourtsPath(pathname)) {
    return null;
  }
  return <Header />;
}

function TopBarGate() {
  const { pathname } = useLocation();
  if (isCourtsPath(pathname)) return null;
  return <TopBar />;
}


// App root component
const App = () => {
  usePrefetch();

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <CartProvider>
                <SearchProvider>
                <AssistantProvider>
                <ErrorBoundary>
                  <MaintenanceGate>
                    <AppLayout />
                  </MaintenanceGate>
                </ErrorBoundary>
                </AssistantProvider>
                </SearchProvider>
              </CartProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

function AppLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <FontLoader />
      <MobileScrollToTop />
      <TopBarGate />
      <HeaderGate />
      <SaleBannerGate />

      <main className="flex-1">
        <Suspense fallback={<div className="flex-1" />}>
          <AppRoutes />
        </Suspense>
      </main>
      <Footer />
      <CartDrawer />
      <WhatsAppButton />
      <ScrollToTopButton />
      <AiAssistant />
    </div>
  );
}

export default App;
